import { useCallback, useMemo } from 'react';
import { UserHierarchyLevel, UserRole } from '@practium/shared';
import type { BindingUserApiResponse } from '@practium/features/users';
import {
	fetchBindingUsers as fetchBindingUsersByType,
	useGetParcellingAccessible,
	useGetProperties,
	useGetPropertiesByBoundUsers,
} from '@practium/features/api';
import type { BindingUsersResponse, PropDTOResponse, ParcelamentoResponse } from '@practium/features/parcelling';

const BINDING_TYPE_BY_ROLE: Record<string, number[]> = {
	[UserRole.Company]: [1, 2],
	[UserRole.Consultant]: [1],
};

const ROLE_BY_HIERARCHY_LEVEL: Record<UserHierarchyLevel, UserRole> = {
	[UserHierarchyLevel.Company]: UserRole.Company,
	[UserHierarchyLevel.Consultant]: UserRole.Consultant,
	[UserHierarchyLevel.Farmer]: UserRole.Farmer,
	[UserHierarchyLevel.Master]: UserRole.Master,
};

const EMPTY_LIST: ParcelamentoResponse = {
	docs: [],
	totalDocs: 0,
	limit: 200,
	totalPages: 0,
	page: 1,
	pagingCounter: 1,
	hasPrevPage: false,
	hasNextPage: false,
	prevPage: null,
	nextPage: null,
};

const mapPropDocs = (docs: any[]) =>
	docs.map((p: any) => ({
		_id: p._id,
		name: p.name,
		city: p.address?.city ?? p.city ?? '',
		state: p.address?.state ?? p.state ?? '',
		userId: p.userId ?? '',
	}));

const buildPropResponse = (docs: ReturnType<typeof mapPropDocs>): PropDTOResponse => ({
	docs,
	pagination: {
		currentPage: 1,
		totalPages: 1,
		totalItems: docs.length,
		itemsPerPage: docs.length,
	},
});

const buildSingleBindingUserResponse = (forBindingUser: BindingUserApiResponse): BindingUsersResponse => ({
	docs: [
		{
			_id: forBindingUser._id,
			name: forBindingUser.name,
			surName: forBindingUser.surName ?? null,
			email: forBindingUser.email,
			role: forBindingUser.role ?? '',
			status: String(forBindingUser.status ?? true),
			avatar: forBindingUser.avatar?.url ? { url: forBindingUser.avatar.url } : undefined,
		},
	],
	pagination: { currentPage: 1, totalPages: 1, totalItems: 1, itemsPerPage: 1 },
});

export interface UseParcellingPageClientParams {
	userRole: UserRole;
	isForBinding: boolean;
	forBindingUserLevel: UserHierarchyLevel | null;
	forBindingId?: string;
	effectiveUserId?: string;
	forBindingUser: BindingUserApiResponse | null;
	canRead: boolean;
	page?: number;
	limit?: number;
}

export const useParcellingPageClient = ({
	userRole,
	isForBinding,
	forBindingUserLevel,
	forBindingId,
	effectiveUserId,
	forBindingUser,
	canRead,
	page = 1,
	limit = 15,
}: UseParcellingPageClientParams) => {
	const { data: parcellingDocs, pagination: parcellingPagination } = useGetParcellingAccessible({
		enabled: canRead,
		page,
		limit,
	});

	const impersonatedRole = isForBinding
		? (forBindingUserLevel ? ROLE_BY_HIERARCHY_LEVEL[forBindingUserLevel] : UserRole.Farmer)
		: userRole;

	const bindTypesForImpersonatedRole = BINDING_TYPE_BY_ROLE[impersonatedRole] ?? [];
	const bindTypesForRealRole = BINDING_TYPE_BY_ROLE[userRole] ?? [];
	const isDeadEndImpersonation = isForBinding && bindTypesForImpersonatedRole.length === 0;
	const canRealUserBind = bindTypesForRealRole.length > 0;

	const bindableRole = isDeadEndImpersonation && canRealUserBind ? userRole : impersonatedRole;
	const hasBoundFarmers = bindTypesForImpersonatedRole.includes(1);
	const effectiveForBindingId = effectiveUserId ?? forBindingId;

	const { refetch: refetchOwnProperties } = useGetProperties({ limit: 200, enabled: false });
	const { refetch: refetchBoundProperties } = useGetPropertiesByBoundUsers({
		bindingType: 1,
		limit: 200,
		enabled: false,
	});

	const mockData = useMemo<ParcelamentoResponse>(() => {
		if (parcellingDocs.length === 0) return EMPTY_LIST;
		return {
			docs: parcellingDocs,
			totalDocs: parcellingPagination?.totalDocs ?? parcellingDocs.length,
			limit: 200,
			totalPages: parcellingPagination?.totalPages ?? Math.max(1, Math.ceil(parcellingDocs.length / 200)),
			page: parcellingPagination?.page ?? 1,
			pagingCounter: parcellingPagination?.page ?? 1,
			hasPrevPage: parcellingPagination?.hasPrevPage ?? false,
			hasNextPage: parcellingPagination?.hasNextPage ?? false,
			prevPage: parcellingPagination?.prevPage ?? null,
			nextPage: parcellingPagination?.nextPage ?? null,
		};
	}, [parcellingDocs, parcellingPagination]);

	const fetchBindingUsers = useCallback(
		async (query: string): Promise<BindingUsersResponse> => {
			if (isDeadEndImpersonation) {
				if (!forBindingUser) {
					return { docs: [], pagination: { currentPage: 1, totalPages: 0, totalItems: 0, itemsPerPage: 10 } };
				}
				return buildSingleBindingUserResponse(forBindingUser);
			}

			if (bindTypesForImpersonatedRole.length === 0) {
				return { docs: [], pagination: { currentPage: 1, totalPages: 0, totalItems: 0, itemsPerPage: 10 } };
			}

			const results = await Promise.all(
				bindTypesForImpersonatedRole.map((bindingType) =>
					fetchBindingUsersByType<any>({
						bindingType,
						forBinding: effectiveForBindingId,
						limit: 100,
						page: 1,
						userNameOrEmail: query || undefined,
					})
				)
			);

			const allDocs = results.flatMap((result) =>
				(result.docs ?? []).map((u: any) => ({
					_id: u._id,
					name: u.name,
					surName: u.surName ?? null,
					email: u.email,
					role: u.role,
					status: String(u.status ?? true),
					avatar: u.avatar?.url ? { url: u.avatar.url } : undefined,
					city: u.address?.city,
				}))
			);

			const unique = allDocs.filter((d, i, a) => a.findIndex((x) => x._id === d._id) === i);

			return {
				ownerName: results[0]?.ownerName,
				docs: unique,
				pagination: {
					currentPage: 1,
					totalPages: 1,
					totalItems: unique.length,
					itemsPerPage: unique.length,
				},
			};
		},
		[isDeadEndImpersonation, forBindingUser, bindTypesForImpersonatedRole, effectiveForBindingId]
	);

	const fetchFarms = useCallback(async (): Promise<PropDTOResponse> => {
		const ownResult = await refetchOwnProperties();
		const boundResult = hasBoundFarmers ? await refetchBoundProperties() : null;

		const combined = [
			...mapPropDocs(ownResult.data?.docs ?? []),
			...mapPropDocs(boundResult?.data?.docs ?? []),
		];
		const docs = combined.filter((d, i, a) => a.findIndex((x) => x._id === d._id) === i);

		return buildPropResponse(docs);
	}, [refetchOwnProperties, refetchBoundProperties, hasBoundFarmers]);

	return {
		mockData,
		fetchFarms,
		fetchBindingUsers,
		effectiveRole: bindableRole,
		autoSelectSelfAsClient: isDeadEndImpersonation,
	};
};
